import React, { useState, useEffect, Component } from 'react';
import cn from 'classnames';
import './Spotlights.scss';

import SpotlightsOverview from './tabs/SpotlightsOverview';
import SpotlightsBrands from './tabs/SpotlightsBrands';
import SpotlightsCreators from './tabs/SpotlightsCreators';
import SpotlightsError from './tabs/SpotlightsError';
import SpotlightsResolved from './tabs/SpotlightsResolved';
import SpotlightsBilling from './tabs/SpotlightsBilling';
import SpotlightsAutomation from './tabs/SpotlightsAutomation';
import SpotlightsModel from './tabs/SpotlightsModel';
import SpotlightsSettings from './tabs/SpotlightsSettings';
import { getTriageCount } from '../../api/spotlights';

const TABS = [
  { key: 'overview', label: 'Overview', Component: SpotlightsOverview },
  { key: 'brands', label: 'Brands', Component: SpotlightsBrands },
  { key: 'creators', label: 'Creators', Component: SpotlightsCreators },
  { key: 'triage', label: 'Triage', Component: SpotlightsError },
  { key: 'resolved', label: 'Resolved', Component: SpotlightsResolved },
  { key: 'billing', label: 'Billing', Component: SpotlightsBilling },
  { key: 'automation', label: 'Ad Automation', Component: SpotlightsAutomation },
  { key: 'model', label: 'Model', Component: SpotlightsModel },
  { key: 'settings', label: 'Settings', Component: SpotlightsSettings }
];

const TRIAGE_COUNT_REFRESH_MS = 60000;

// Keep a thrown error inside one tab from blanking the whole admin app (no global
// error boundary exists upstream). Resets when the active tab changes.
class TabErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tabKey !== this.props.tabKey && this.state.error) this.setState({ error: null });
  }

  render() {
    if (this.state.error) {
      return <div className='sl-error'>Something went wrong rendering this tab: {String(this.state.error.message || this.state.error)}</div>;
    }
    return this.props.children;
  }
}

const Spotlights = () => {
  const [tab, setTab] = useState('overview');
  // Cross-tab navigation: a tab (e.g. Overview's attention cards) can jump to another tab
  // and hand it a preset (e.g. a Brands filter). navParams is consumed by the target tab's
  // initial state on mount, and cleared when the user manually clicks a tab.
  const [navParams, setNavParams] = useState({});
  const [triageCount, setTriageCount] = useState(0);

  // Poll the open-triage count so the tab badge stays fresh without opening the tab.
  useEffect(() => {
    let active = true;
    const tick = () => getTriageCount().then(r => active && setTriageCount(r.count || 0)).catch(() => {});
    tick();
    const t = setInterval(tick, TRIAGE_COUNT_REFRESH_MS);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);

  const navigate = (tabKey, params = {}) => {
    setNavParams(params);
    setTab(tabKey);
  };
  const onTabClick = key => {
    setNavParams({});
    setTab(key);
  };
  const ActiveTab = (TABS.find(t => t.key === tab) || TABS[0]).Component;

  return (
    <div className='spotlights-admin-outer'>
      <div className='spotlights-admin-header'>
        <div className='spotlights-admin-title'>Spotlights</div>
        <div className='spotlights-admin-tabs'>
          {TABS.map(t => (
            <div key={t.key} className={cn('spotlights-admin-tab', { active: tab === t.key })} onClick={() => onTabClick(t.key)}>
              {t.label}
              {t.key === 'triage' && triageCount > 0 && <span className='spotlights-admin-tab-badge'>{triageCount}</span>}
            </div>
          ))}
        </div>
      </div>
      <div className='spotlights-admin-body'>
        <TabErrorBoundary tabKey={tab}>
          <ActiveTab navigate={navigate} navParams={navParams} />
        </TabErrorBoundary>
      </div>
    </div>
  );
};

export default Spotlights;
